import type { ActivityContext, ActivityHandle } from '../../core/activity';
import levelsData from './levels.json';

interface Level {
  level: number;
  maxNumber: number;
  values: number[];
}

interface LevelsFile {
  numberOfFish: number;
  levels: Level[];
}

const DATA = levelsData as LevelsFile;
const LEVELS = DATA.levels;

const DOT_PATTERNS: Record<number, [number, number][]> = {
  0: [],
  1: [[1, 1]],
  2: [[0, 0], [2, 2]],
  3: [[0, 0], [1, 1], [2, 2]],
  4: [[0, 0], [0, 2], [2, 0], [2, 2]],
  5: [[0, 0], [0, 2], [1, 1], [2, 0], [2, 2]],
  6: [[0, 0], [0, 2], [1, 0], [1, 2], [2, 0], [2, 2]],
  7: [[0, 0], [0, 2], [1, 0], [1, 1], [1, 2], [2, 0], [2, 2]],
  8: [[0, 0], [0, 1], [0, 2], [1, 0], [1, 2], [2, 0], [2, 1], [2, 2]],
  9: [[0, 0], [0, 1], [0, 2], [1, 0], [1, 1], [1, 2], [2, 0], [2, 1], [2, 2]],
};

function buildDotsGrid(value: number): string {
  const positions = DOT_PATTERNS[Math.min(value, 9)] ?? [];
  const cells: string[] = [];
  for (let r = 0; r < 3; r++) {
    for (let c = 0; c < 3; c++) {
      const has = positions.some((p) => p[0] === r && p[1] === c);
      cells.push(has ? '<div class="rcnt__dot"></div>' : '<div></div>');
    }
  }
  return cells.join('');
}

export function mountReverseCount(ctx: ActivityContext): ActivityHandle {
  const level = LEVELS.find((l) => l.level === ctx.level) ?? LEVELS[0];
  if (!level) {
    ctx.root.innerHTML = '<p>Nível não encontrado.</p>';
    return { unmount: () => { ctx.root.innerHTML = ''; } };
  }

  const totalFish = DATA.numberOfFish;
  let fishCaught = 0;
  let targetN = 0;
  let leftVal = 0;
  let rightVal = 0;
  let tuxPos = 0; // current block index
  let buttonsBlocked = false;

  const page = document.createElement('div');
  page.className = 'activity-page';
  page.innerHTML = `
    <header class="activity-bar">
      <button class="activity-bar__back" type="button" aria-label="Voltar ao hub">← Voltar</button>
      <div class="activity-bar__title">Conte os blocos</div>
      <div class="activity-bar__level">Nível ${level.level} de ${LEVELS.length}</div>
    </header>
    <section class="activity-stage rcnt">
      <div class="feedback" aria-live="polite"></div>
      <p class="rcnt__progress" aria-live="polite"></p>
      <p class="rcnt__hint">Quantos blocos de gelo entre o pinguim e o peixe? Monte o número no dominó.</p>
      <div class="rcnt__field-wrap"><div class="rcnt__field"></div></div>
      <div class="rcnt__domino"></div>
      <div class="rcnt__controls"></div>
      <div class="rcnt__sum" aria-live="polite"></div>
      <div class="rcnt__actions">
        <button class="btn btn--primary rcnt__confirm" type="button">Confirmar</button>
      </div>
    </section>
  `;
  ctx.root.appendChild(page);

  const progressEl = page.querySelector<HTMLElement>('.rcnt__progress')!;
  const fieldEl = page.querySelector<HTMLElement>('.rcnt__field')!;
  const dominoEl = page.querySelector<HTMLElement>('.rcnt__domino')!;
  const controlsEl = page.querySelector<HTMLElement>('.rcnt__controls')!;
  const sumEl = page.querySelector<HTMLElement>('.rcnt__sum')!;
  const feedback = page.querySelector<HTMLElement>('.feedback')!;
  const confirmBtn = page.querySelector<HTMLButtonElement>('.rcnt__confirm')!;
  page.querySelector<HTMLElement>('.activity-bar__back')!.addEventListener('click', () => ctx.onExit());
  confirmBtn.addEventListener('click', validate);

  function startFish(): void {
    targetN = level.values[Math.floor(Math.random() * level.values.length)]!;
    leftVal = 0;
    rightVal = 0;
    tuxPos = 0;
    buttonsBlocked = false;
    renderAll();
  }

  function renderAll(): void {
    renderProgress();
    renderField();
    renderDomino();
    renderControls();
    renderSum();
  }

  function renderProgress(): void {
    progressEl.textContent = `Peixe ${fishCaught + 1} de ${totalFish}`;
  }

  function renderField(): void {
    fieldEl.innerHTML = '';
    const blockW = 44;
    const gap = 4;
    // Total = N blocos entre Tux (pos 0) e peixe (pos N)
    for (let i = 0; i < targetN; i++) {
      const b = document.createElement('div');
      b.className = 'rcnt__block';
      b.textContent = '🧊';
      fieldEl.appendChild(b);
    }
    // Tux: position based on tuxPos
    const tux = document.createElement('div');
    tux.className = 'rcnt__tux';
    tux.textContent = '🐧';
    tux.style.left = `${12 + tuxPos * (blockW + gap)}px`;
    fieldEl.appendChild(tux);
    // Fish: at end (after the N-th block)
    const fish = document.createElement('div');
    fish.className = 'rcnt__fish';
    fish.textContent = '🐠';
    fish.style.left = `${12 + targetN * (blockW + gap) + 6}px`;
    fieldEl.appendChild(fish);
  }

  function renderDomino(): void {
    dominoEl.innerHTML = `
      <div class="rcnt__half">${buildDotsGrid(leftVal)}</div>
      <div class="rcnt__half">${buildDotsGrid(rightVal)}</div>
    `;
  }

  function renderControls(): void {
    controlsEl.innerHTML = '';
    const sides: { label: string; get: () => number; set: (v: number) => void }[] = [
      { label: 'Esquerda', get: () => leftVal, set: (v) => { leftVal = v; } },
      { label: 'Direita', get: () => rightVal, set: (v) => { rightVal = v; } },
    ];
    for (const s of sides) {
      const wrap = document.createElement('div');
      wrap.className = 'rcnt__side';
      const minus = document.createElement('button');
      minus.type = 'button';
      minus.className = 'rcnt__btn';
      minus.textContent = '−';
      minus.disabled = buttonsBlocked || s.get() <= 0;
      minus.addEventListener('click', () => {
        if (buttonsBlocked) return;
        if (s.get() <= 0) return;
        s.set(s.get() - 1);
        ctx.audio.play('click');
        renderAll();
      });
      const val = document.createElement('span');
      val.className = 'rcnt__side-val';
      val.textContent = String(s.get());
      val.setAttribute('aria-label', `${s.label}: ${s.get()}`);
      const plus = document.createElement('button');
      plus.type = 'button';
      plus.className = 'rcnt__btn';
      plus.textContent = '+';
      plus.disabled = buttonsBlocked || s.get() >= level.maxNumber;
      plus.addEventListener('click', () => {
        if (buttonsBlocked) return;
        if (s.get() >= level.maxNumber) return;
        s.set(s.get() + 1);
        ctx.audio.play('click');
        renderAll();
      });
      wrap.appendChild(minus);
      wrap.appendChild(val);
      wrap.appendChild(plus);
      controlsEl.appendChild(wrap);
    }
  }

  function renderSum(): void {
    sumEl.innerHTML = `${leftVal} + ${rightVal} = <strong>${leftVal + rightVal}</strong>`;
  }

  function validate(): void {
    if (buttonsBlocked) return;
    const sum = leftVal + rightVal;
    if (sum === targetN) {
      buttonsBlocked = true;
      ctx.audio.play('correct');
      showFeedback('ok');
      // anima Tux andando: incrementa tuxPos a cada 250ms até atingir targetN
      animateWalk();
    } else {
      ctx.audio.play('wrong');
      showFeedback('err');
    }
  }

  function animateWalk(): void {
    let steps = 0;
    const step = (): void => {
      steps++;
      tuxPos = steps;
      const tux = fieldEl.querySelector<HTMLElement>('.rcnt__tux');
      if (tux) tux.style.left = `${12 + tuxPos * (44 + 4)}px`;
      if (steps < targetN) {
        window.setTimeout(step, 250);
      } else {
        window.setTimeout(() => {
          fishCaught++;
          if (fishCaught < totalFish) startFish();
          else finishLevel();
        }, 600);
      }
    };
    if (targetN > 0) step();
    else {
      // 0 blocks: já tá no peixe
      window.setTimeout(() => {
        fishCaught++;
        if (fishCaught < totalFish) startFish();
        else finishLevel();
      }, 600);
    }
  }

  function showFeedback(kind: 'ok' | 'err'): void {
    feedback.textContent = kind === 'ok' ? '✓' : '✗';
    feedback.className = `feedback feedback--show feedback--${kind}`;
    window.setTimeout(() => feedback.classList.remove('feedback--show'), 500);
  }

  function finishLevel(): void {
    ctx.audio.play('win');
    ctx.onComplete(level.level, fishCaught);
    const stage = page.querySelector<HTMLElement>('.activity-stage')!;
    stage.innerHTML = `
      <h2 class="activity-finish__title">Nível ${level.level} concluído!</h2>
      <p class="activity-finish__text">O pinguim comeu ${fishCaught} de ${totalFish} peixes.</p>
      <div class="activity-finish__actions">
        <button class="btn btn--secondary" type="button" data-act="hub">Voltar ao hub</button>
        ${level.level < LEVELS.length
          ? `<button class="btn btn--primary" type="button" data-act="next">Próximo nível</button>`
          : ''}
      </div>
    `;
    stage.querySelector<HTMLElement>('[data-act="hub"]')!.addEventListener('click', () => ctx.onExit());
    const next = stage.querySelector<HTMLElement>('[data-act="next"]');
    if (next) {
      next.addEventListener('click', () => {
        window.location.hash = `/reversecount/${level.level + 1}`;
      });
    }
  }

  startFish();

  return {
    unmount(): void {
      ctx.root.innerHTML = '';
    },
  };
}
